import {match} from 'ts-pattern';
import {DeepPartial} from 'utility-types';

import {isNil} from 'ramda';

import {GetServiceOrderVariantPaymentTypeApiResponse} from '@dms/api/workshop';

import {Nullish} from 'shared/web';

import {FormType} from '../types/ServiceOrderVariant';

type TemplateIdResolver = (templateId: string | Nullish) => string | undefined;

interface InvoiceTemplateResolvers {
  resolveStandardInvoiceTemplateId: TemplateIdResolver;
  resolveInternalInvoiceTemplateId: TemplateIdResolver;
}

// Internal payment is always initiated automatically (the checkbox is read-only in the form), so
// the server value is intentionally overridden — matches INTERNAL_AUTO_INITIATE_PAYMENT in
// buildPaymentTypePatchBody.
const INTERNAL_AUTO_INITIATE_PAYMENT = true;

// A disallowed payment type keeps its document configuration untouched, so healing never rewrites a
// configuration the operator cannot see on the collapsed card. Only a missing or null flag is
// normalized to false, because the form schema requires a real boolean. Any other malformed value is
// left to fail validation rather than be coerced into a save that wipes the stored configuration.
const withInvoiceTemplateId = <
  T extends {isAllowed?: boolean | null; invoice?: {templateId?: string | null} | null},
>(
  payment: T,
  resolveTemplateId: TemplateIdResolver
) =>
  match(payment.isAllowed)
    .with(true, () => ({
      ...payment,
      invoice: {
        ...payment.invoice,
        templateId: resolveTemplateId(payment.invoice?.templateId),
      },
    }))
    .when(isNil, () => ({...payment, isAllowed: false}))
    .otherwise(() => payment);

export function buildPaymentTypeDefaultValues(
  paymentType: GetServiceOrderVariantPaymentTypeApiResponse | Nullish,
  resolvers: InvoiceTemplateResolvers
): DeepPartial<FormType>['paymentType'] {
  return {
    defaultPaymentType: paymentType?.defaultPaymentType ?? undefined,
    cashPayment: withInvoiceTemplateId(
      paymentType?.cashPayment ?? {isAllowed: false},
      resolvers.resolveStandardInvoiceTemplateId
    ),
    cashOnDeliveryPayment: withInvoiceTemplateId(
      paymentType?.cashOnDeliveryPayment ?? {isAllowed: false},
      resolvers.resolveStandardInvoiceTemplateId
    ),
    cardPayment: withInvoiceTemplateId(
      paymentType?.cardPayment ?? {isAllowed: false},
      resolvers.resolveStandardInvoiceTemplateId
    ),
    bankPayment: withInvoiceTemplateId(
      paymentType?.bankPayment ?? {isAllowed: false},
      resolvers.resolveStandardInvoiceTemplateId
    ),
    internalPayment: {
      ...withInvoiceTemplateId(
        paymentType?.internalPayment ?? {isAllowed: false},
        resolvers.resolveInternalInvoiceTemplateId
      ),
      isAutoInitiatePayment: INTERNAL_AUTO_INITIATE_PAYMENT,
    },
  };
}
